import React from 'react';

import styled from 'styled-components';

import AICISummaryCard from './AICISummaryCard';
import { AI_CI_PERFORMANCE, OVERALL_VIEW_KEY } from './constants';
import { categoryLabel } from './helpers';
import { CompanyTableFilters } from './types';

const Container = styled.div`
  display: flex;
  gap: 20px;
`;

const Count = styled.span`
  font-size: 28px;
  line-height: 1;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const Unit = styled.span`
  font-size: 14px;
  font-family: var(--font-regular);
  color: var(--text-primary);
`;

const CategorySubtitle = styled.div`
  padding-top: 5px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

type Props = {
  leadingCount: number;
  laggingCount: number;
  topStrongest: string | null;
  topWeakest: string | null;
  filters: CompanyTableFilters;
  onFiltersChange: (filters: CompanyTableFilters) => void;
  categoryLabel?: string;
  activeTab: string;
};

const AICICompanySummary = ({
  leadingCount,
  laggingCount,
  topStrongest,
  topWeakest,
  filters,
  onFiltersChange,
  categoryLabel: category,
  activeTab,
}: Props) => {
  const fitContent = activeTab !== OVERALL_VIEW_KEY;
  const strongestLabel = categoryLabel(topStrongest) ?? '-';
  const weakestLabel = categoryLabel(topWeakest) ?? '-';

  const togglePerformance = (value: string) =>
    onFiltersChange({
      ...filters,
      performance: filters.performance.includes(value) ? [] : [value],
      strongestCategories: [],
      weakestCategories: [],
    });

  const toggleStrongest = () => {
    if (!topStrongest) return;
    onFiltersChange({
      ...filters,
      performance: [],
      weakestCategories: [],
      strongestCategories: filters.strongestCategories.includes(topStrongest) ? [] : [topStrongest],
    });
  };

  const toggleWeakest = () => {
    if (!topWeakest) return;
    onFiltersChange({
      ...filters,
      performance: [],
      strongestCategories: [],
      weakestCategories: filters.weakestCategories.includes(topWeakest) ? [] : [topWeakest],
    });
  };

  return (
    <Container>
      <AICISummaryCard
        title={category ? `Leading (${category})` : 'Leading'}
        fitContent={fitContent}
        iconClassName="mint"
        iconImageSrc="/icons/emoji-events.svg"
        subtitle={
          <>
            <Count>{leadingCount}</Count>
            <Unit>communities</Unit>
          </>
        }
        selected={filters.performance.includes(AI_CI_PERFORMANCE.LEADING)}
        onClick={() => togglePerformance(AI_CI_PERFORMANCE.LEADING)}
      />
      <AICISummaryCard
        title={category ? `Lagging (${category})` : 'Lagging'}
        fitContent={fitContent}
        iconClassName="gold"
        iconImageSrc="/icons/sentiment-dissatisfied.svg"
        subtitle={
          <>
            <Count>{laggingCount}</Count>
            <Unit>communities</Unit>
          </>
        }
        selected={filters.performance.includes(AI_CI_PERFORMANCE.LAGGING)}
        onClick={() => togglePerformance(AI_CI_PERFORMANCE.LAGGING)}
      />
      {!category && (
        <>
          <AICISummaryCard
            title="Strongest Category"
            iconClassName="mint"
            iconImageSrc="/icons/sentiment-satisfied-green.svg"
            subtitle={<CategorySubtitle title={strongestLabel}>{strongestLabel}</CategorySubtitle>}
            selected={Boolean(topStrongest) && filters.strongestCategories.includes(topStrongest as string)}
            onClick={toggleStrongest}
          />
          <AICISummaryCard
            title="Weakest Categories"
            iconClassName="pink"
            iconImageSrc="/icons/ads-click.svg"
            subtitle={<CategorySubtitle title={weakestLabel}>{weakestLabel}</CategorySubtitle>}
            selected={Boolean(topWeakest) && filters.weakestCategories.includes(topWeakest as string)}
            onClick={toggleWeakest}
          />
        </>
      )}
    </Container>
  );
};

export default AICICompanySummary;
